import { FilterFieldValue } from '../../../covance-filter/covance-filter.models';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { deserialize, deserializeArray } from 'santee-dcts';
import { CultureInfo } from '../../../core/culture-info';
import { Settings } from '../../../core/settings';
import { Category, CategoryNaturalKey, defaultReportSort, Report, ReportsReadModel } from '../../../documents';
import { getReportType } from '../../../documents/document-type-resolver';
import { DocumentsStorage } from '../../../documents/documents-storage';
import { prepareFilterUrlParams } from '../../../documents/prepare-filter-url-params';
import { UploadedDocumentsRequestCreator } from './uploaded-documents-request-creator';
import { UploadStorage } from './upload-storage';
import { ActivityLogger } from '../../../activities/activity-logger';

@Injectable()
export class UploadedDocumentsStorage implements DocumentsStorage, UploadStorage {
    private readonly apiPath = 'admin/uploaded-documents';
    private readonly refresher = new BehaviorSubject<boolean>(true);

    constructor(
        private readonly http: HttpClient,
        private readonly settings: Settings,
        private readonly uploadRequestCreator: UploadedDocumentsRequestCreator,
        private readonly activityLogger: ActivityLogger,
    ) { }

    public loadDocumentsByCategory(category: Category, filterParameters: FilterFieldValue[]): Observable<Report[]> {

        const filtersParams = prepareFilterUrlParams(filterParameters);

        return this.refresher.pipe(
            switchMap(() => this.http
                .get(`${this.settings.apiUrl}/${this.apiPath}/${category.categoryId}`, {params: filtersParams})),
            map(rawData => this.getUploadedDocsForCategory(rawData, category))
        );
    }

    public forceLoadDocuments() {
        this.refresher.next(true);
    }

    public create<T>(categoryId: CategoryNaturalKey, files: Map<File, CultureInfo>, documentMetadata: T): Observable<HttpEvent<Object>> {
        this.activityLogger.logUploadActivity(categoryId);

        return this.uploadRequestCreator.createPost(`${this.settings.apiUrl}/${this.apiPath}/${categoryId}`, files, documentMetadata);
    }

    public update(categoryId: CategoryNaturalKey, files: Map<File, CultureInfo>, documentId: string, auditTrailReason: string): Observable<HttpEvent<Object>> {
        this.activityLogger.logEditFileActivity(documentId);

        return this.uploadRequestCreator.createPut(`${this.settings.apiUrl}/admin/uploaded-documents/${categoryId}/update/${documentId}`, files, auditTrailReason);
    }

    public changeVisibility(category: Category, request: VisibilityDocumentRequest): Promise<Object> {
        this.activityLogger.logHideUnhideDocumentActivity(request.documentId);

        return this.http
            .post(`${this.settings.apiUrl}/${this.apiPath}/${category.categoryId}/visibility`, request)
            .toPromise();
    }

    private getUploadedDocsForCategory(response: Object, category: Category) {
        const readModel = deserialize(response, ReportsReadModel);

        const reportType = getReportType(category.categoryId);
        let reports = deserializeArray(readModel.reports, reportType) as Report[];

        for (let report of reports) {
            report.category = category;
        }

        reports = reports.sort((a, b) => defaultReportSort(a, b));

        return reports;
    }
}

export interface VisibilityDocumentRequest {
    documentId: string;
    isHidden: boolean;
    reason: string;
    reportId: string;
}
